import { useEffect, useMemo, useState } from "react";
import {
    BellRing,
    CalendarClock,
    Filter,
    Loader2,
    Search,
    XCircle,
    Hourglass,
    AlertTriangle,
    Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ReminderAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const STATUS_FILTERS = [
    { key: "active", label: "Active", statuses: "scheduled,snoozed" },
    { key: "scheduled", label: "Scheduled", statuses: "scheduled" },
    { key: "snoozed", label: "Snoozed", statuses: "snoozed" },
    { key: "dispatched", label: "Sent", statuses: "dispatched" },
    { key: "cancelled", label: "Cancelled", statuses: "cancelled" },
    { key: "all", label: "All", statuses: "" },
];

const DUE_WINDOWS = [
    { key: "__any__", label: "Any due date" },
    { key: "overdue", label: "Overdue" },
    { key: "this_week", label: "Due this week" },
    { key: "this_month", label: "Due this month" },
];

const TYPE_LABEL = {
    service_due: "Service due",
    overdue: "Overdue follow-up",
    dvi_followup: "DVI follow-up",
    amc_renewal: "AMC renewal",
};

function StatusPill({ status, isOverdue = false }) {
    const palette = {
        scheduled: isOverdue
            ? "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/15 dark:text-rose-300"
            : "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-200",
        snoozed: "border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-500/40 dark:bg-sky-500/15 dark:text-sky-300",
        dispatched:
            "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300",
        cancelled:
            "border-zinc-300 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-400",
        responded:
            "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300",
    };
    const label = isOverdue ? "OVERDUE" : status.toUpperCase();
    return (
        <span
            data-testid={`reminder-status-${status}`}
            className={cn(
                "inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider",
                palette[status] || palette.cancelled,
            )}
        >
            {label}
        </span>
    );
}

function fmtDate(iso, tz) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", {
            timeZone: tz || "Asia/Kolkata",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (_e) {
        return iso;
    }
}

export default function RemindersPage() {
    const { permissions, tenant } = useAuth();
    const canWrite = (permissions || []).includes("reminder.write");

    const [filter, setFilter] = useState(STATUS_FILTERS[0]);
    const [dueWindow, setDueWindow] = useState("__any__");
    const [search, setSearch] = useState("");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [tick, setTick] = useState(0);

    const [snoozeTarget, setSnoozeTarget] = useState(null);
    const [snoozeDays, setSnoozeDays] = useState("14");
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [acting, setActing] = useState(false);

    const tz = "Asia/Kolkata";
    const nowMs = Date.now();

    useEffect(() => {
        let alive = true;
        setLoading(true);
        const params = { page: 1, page_size: 100 };
        if (filter.statuses) params.status = filter.statuses;
        if (dueWindow && dueWindow !== "__any__") params.due_window = dueWindow;
        ReminderAPI.list(params)
            .then((d) => {
                if (!alive) return;
                setItems(d.items || []);
                setTotal(d.total || 0);
            })
            .catch((e) => {
                if (!alive) return;
                toast.error(extractError(e));
                setItems([]);
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [filter.key, filter.statuses, dueWindow, tick]);

    const filtered = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.toLowerCase();
        return items.filter((r) => {
            const p = r.payload || {};
            return (
                (p.customer_name || "").toLowerCase().includes(q) ||
                (p.vehicle_label || "").toLowerCase().includes(q)
            );
        });
    }, [items, search]);

    async function doSnooze() {
        const days = Number(snoozeDays);
        if (!Number.isFinite(days) || days <= 0) {
            toast.error("Snooze days must be a positive number");
            return;
        }
        const target = new Date(Date.now() + days * 86400_000).toISOString();
        try {
            setActing(true);
            await ReminderAPI.snooze(snoozeTarget.id, target);
            toast.success(`Snoozed ${days} day${days === 1 ? "" : "s"}`);
            setSnoozeTarget(null);
            setTick((t) => t + 1);
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setActing(false);
        }
    }

    async function doCancel() {
        try {
            setActing(true);
            await ReminderAPI.cancel(cancelTarget.id, cancelReason);
            toast.success("Reminder cancelled");
            setCancelTarget(null);
            setCancelReason("");
            setTick((t) => t + 1);
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setActing(false);
        }
    }

    return (
        <div className="animate-fade-in space-y-6" data-testid="reminders-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                        {tenant?.name} · Engagement
                    </div>
                    <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        <BellRing className="h-7 w-7 text-blue-500" />
                        Service reminders
                    </h1>
                    <p className="mt-1 max-w-2xl text-sm text-zinc-500">
                        Time-based engagement. Auto-scheduled 6 months after every job delivery
                        and dispatched through WhatsApp / Email when due (stub provider for now).
                    </p>
                </div>
                <div
                    className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs leading-tight text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400"
                    data-testid="reminders-total"
                >
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        Total in view
                    </div>
                    <div className="font-display text-xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">
                        {total}
                    </div>
                </div>
            </header>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex flex-wrap items-center gap-1" data-testid="reminders-status-filter">
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.key}
                            type="button"
                            data-testid={`filter-status-${f.key}`}
                            onClick={() => setFilter(f)}
                            className={cn(
                                "rounded border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors",
                                filter.key === f.key
                                    ? "border-blue-500 bg-blue-500/10 text-blue-700 dark:text-blue-300"
                                    : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800",
                            )}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 sm:ml-auto">
                    <Select value={dueWindow} onValueChange={setDueWindow}>
                        <SelectTrigger className="w-44" data-testid="filter-due-window">
                            <SelectValue placeholder="Any due date" />
                        </SelectTrigger>
                        <SelectContent>
                            {DUE_WINDOWS.map((w) => (
                                <SelectItem key={w.key} value={w.key}>
                                    {w.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="relative w-56">
                        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
                        <Input
                            data-testid="reminders-search"
                            placeholder="Customer or vehicle…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8"
                        />
                    </div>
                </div>
            </div>

            <Card className="overflow-hidden" data-testid="reminders-table-card">
                {loading ? (
                    <div className="flex h-40 items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div
                        className="flex flex-col items-center justify-center gap-2 py-14 text-center"
                        data-testid="reminders-empty"
                    >
                        <Inbox className="h-8 w-8 text-zinc-400" />
                        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                            No reminders match this view
                        </div>
                        <p className="max-w-md text-xs text-zinc-500">
                            Service reminders are scheduled automatically when a job card hits
                            <code className="mx-1 rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[11px] text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                                delivered
                            </code>
                            status.
                        </p>
                    </div>
                ) : (
                    <table className="w-full table-auto text-sm">
                        <thead className="border-b border-zinc-200 bg-zinc-50/60 text-left text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/40">
                            <tr>
                                <th className="px-3 py-2.5">Customer / Vehicle</th>
                                <th className="px-3 py-2.5">Type</th>
                                <th className="px-3 py-2.5">Due</th>
                                <th className="px-3 py-2.5">Channels</th>
                                <th className="px-3 py-2.5">Status</th>
                                <th className="px-3 py-2.5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                            {filtered.map((r) => {
                                const isActive =
                                    r.status === "scheduled" || r.status === "snoozed";
                                const dueMs = r.due_at_utc ? new Date(r.due_at_utc).getTime() : null;
                                const isOverdue =
                                    r.status === "scheduled" && dueMs != null && dueMs < nowMs;
                                return (
                                    <tr
                                        key={r.id}
                                        data-testid={`reminder-row-${r.id}`}
                                        className="transition-colors hover:bg-zinc-50/60 dark:hover:bg-zinc-900/30"
                                    >
                                        <td className="px-3 py-2.5">
                                            <div className="font-medium text-zinc-900 dark:text-zinc-100">
                                                {r.payload?.customer_name || "—"}
                                            </div>
                                            <div className="font-mono text-[11px] text-zinc-500">
                                                {r.payload?.vehicle_label || r.vehicle_id?.slice(-6)}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span className="rounded border border-zinc-200 bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
                                                {TYPE_LABEL[r.reminder_type] || r.reminder_type}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 font-mono text-[11px] text-zinc-700 dark:text-zinc-300">
                                            {fmtDate(r.due_at_utc, tz)}
                                        </td>
                                        <td className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                            {(r.channel_preferences || []).join(" · ") || "—"}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <StatusPill status={r.status} isOverdue={isOverdue} />
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {canWrite && isActive && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setSnoozeTarget(r)}
                                                        data-testid={`reminder-snooze-${r.id}`}
                                                        className="gap-1 text-xs"
                                                    >
                                                        <Hourglass className="h-3 w-3" />
                                                        Snooze
                                                    </Button>
                                                )}
                                                {canWrite &&
                                                    r.status !== "cancelled" &&
                                                    r.status !== "responded" && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => setCancelTarget(r)}
                                                            data-testid={`reminder-cancel-${r.id}`}
                                                            className="gap-1 text-xs text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
                                                        >
                                                            <XCircle className="h-3 w-3" />
                                                            Cancel
                                                        </Button>
                                                    )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </Card>

            {/* Snooze dialog */}
            <Dialog
                open={!!snoozeTarget}
                onOpenChange={(o) => !o && setSnoozeTarget(null)}
            >
                <DialogContent data-testid="snooze-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Hourglass className="h-4 w-4" />
                            Snooze reminder
                        </DialogTitle>
                        <DialogDescription>
                            Push the next send out by N days. Status will change to{" "}
                            <code className="rounded bg-zinc-100 px-1 dark:bg-zinc-800">snoozed</code>.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <div className="text-sm font-medium">
                                {snoozeTarget?.payload?.customer_name || "—"}
                            </div>
                            <div className="font-mono text-[11px] text-zinc-500">
                                {snoozeTarget?.payload?.vehicle_label}
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="snooze-days">Days to snooze</Label>
                            <Input
                                id="snooze-days"
                                data-testid="snooze-days-input"
                                type="number"
                                min={1}
                                max={365}
                                value={snoozeDays}
                                onChange={(e) => setSnoozeDays(e.target.value)}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={() => setSnoozeTarget(null)}
                            disabled={acting}
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="snooze-confirm"
                            onClick={doSnooze}
                            disabled={acting}
                            className="gap-1"
                        >
                            {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            Snooze
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel dialog */}
            <Dialog
                open={!!cancelTarget}
                onOpenChange={(o) => !o && setCancelTarget(null)}
            >
                <DialogContent data-testid="cancel-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-rose-500" />
                            Cancel reminder
                        </DialogTitle>
                        <DialogDescription>
                            The customer will no longer receive a service-due nudge for this
                            vehicle. This is reversible by creating a new reminder.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <div className="text-sm font-medium">
                                {cancelTarget?.payload?.customer_name || "—"}
                            </div>
                            <div className="font-mono text-[11px] text-zinc-500">
                                {cancelTarget?.payload?.vehicle_label}
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="cancel-reason">Reason (optional)</Label>
                            <Textarea
                                id="cancel-reason"
                                data-testid="cancel-reason-input"
                                placeholder="e.g. Customer moved to another workshop"
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                maxLength={240}
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={() => setCancelTarget(null)}
                            disabled={acting}
                        >
                            Keep
                        </Button>
                        <Button
                            variant="destructive"
                            data-testid="cancel-confirm"
                            onClick={doCancel}
                            disabled={acting}
                            className="gap-1"
                        >
                            {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            Cancel reminder
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
